/**
 * The last surface: a render crash that does not take the page with it.
 *
 * Without one, a component that throws leaves a white screen and a console the player cannot
 * see. What matters here is not the styling of the fallback — it is that **the two ways out
 * are different actions and both are offered**: *try again* re-mounts the tree, which is the
 * right move for a transient failure (a chapter that arrived mid-render, a bad cache entry
 * that a refetch will replace); *reload* throws away all client state, which is the only
 * thing that helps when the module graph itself is stale after a deploy.
 *
 * A class, because that is still the only way to catch a render error in React 19 — hooks
 * cannot express `getDerivedStateFromError`. It is the one class component in the app.
 *
 * **It is mounted around the router, not around everything.** The toast host and the feedback
 * dialog are siblings of it in `App`, so the crash screen can still offer *report this* and
 * still raise a toast — a fallback that has to live without the app's own affordances is a
 * fallback that gets built twice.
 *
 * The fallback's markup is a function component below rather than JSX in `render()`, for one
 * reason: a class cannot call `useT()`. If the thing that threw *was* the i18n provider,
 * `useT` answers with its bundled-English default (`i18n/context.ts`) and the crash screen
 * still draws — which is the whole job.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useT } from '@/i18n/context';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Offered as *report this*. Given the error's message, so feedback arrives pre-filled. */
  onReport?: (error: Error) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // A crash is one of the two cases where the developer console is the right output (the
    // other is `turn/runTurn.ts` dropping an unreadable frame): there is no request id to
    // correlate and no server call to attach it to.
    console.error('Unhandled render error', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <CrashScreen
        error={error}
        onRetry={() => {
          this.setState({ error: null });
        }}
        onReport={this.props.onReport}
      />
    );
  }
}

function CrashScreen({
  error,
  onRetry,
  onReport,
}: {
  error: Error;
  onRetry: () => void;
  onReport?: (error: Error) => void;
}) {
  const t = useT();

  return (
    <main className="mx-auto flex min-h-dvh max-w-[var(--measure)] flex-col justify-center gap-4 p-8">
      <h1 className="text-fg text-2xl font-semibold tracking-tight">{t('ui.error.crash_title')}</h1>
      <p className="text-fg-muted" style={{ fontSize: 'var(--text-action)' }}>
        {t('ui.error.crash_body')}
      </p>
      {/* The developer's own words, untranslated by definition. */}
      <p className="text-fg-faint font-mono" style={{ fontSize: 'var(--text-meta)' }}>
        {error.message}
      </p>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onRetry}
          className="border-accent text-accent hover:bg-accent hover:text-bg border px-4 py-2 font-mono transition-colors"
          style={{ fontSize: 'var(--text-action)' }}
        >
          {t('ui.common.retry')}
        </button>
        <button
          type="button"
          onClick={() => {
            window.location.reload();
          }}
          className="border-border text-fg-muted hover:border-accent hover:text-accent border px-4 py-2 font-mono transition-colors"
          style={{ fontSize: 'var(--text-action)' }}
        >
          {t('ui.error.reload')}
        </button>
        {onReport ? (
          <button
            type="button"
            onClick={() => {
              onReport(error);
            }}
            className="text-fg-faint hover:text-fg px-2 py-2 font-mono transition-colors"
            style={{ fontSize: 'var(--text-action)' }}
          >
            {t('ui.feedback.report_error')}
          </button>
        ) : null}
      </div>
    </main>
  );
}
